import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import { MetaDescription, MetaKeywords } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { ContentWrapper } from "@insite/content-library/Widgets/Basic/RichContent";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import MobiusPage from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledClickable, TypographyWrapper } from "@waytek/MobiusOverrides/Link/Link";
import SchemaForBlogDetail from "@waytek/Overrides/Components/SchemaForBlogDetail";
import { colorStrings, fontFamily, fontFamilyAlt, mediaQueriesBP } from "@waytek/Utilities/variables";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    name = "name",
    displayName = "displayName",
    publishDate = "publishDate",
    author = "author",
    content = "content",
    summary = "summary",
    thumbnail = "thumbnail",
    teaser = "teaser",
}

interface OwnProps extends PageProps {
    fields: {
        [fields.title]: string;
        [fields.name]: string;
        [fields.displayName]: string;
        [fields.publishDate]: string;
        [fields.author]: string;
        [fields.content]: string;
        [fields.summary]: string;
        [fields.thumbnail]: string;
        [fields.teaser]: string;
    };
}

const blogTitleStyles = css`
    order: 1;
    padding-top: 0;
    padding-bottom: 0;
`;

const pageTitle = css`
    width: 100%;
    border-bottom: 4px solid ${getColor(colorStrings.commonGlobalIndustrialTaupe)};
    padding: 40px 0 10px;
    margin: 0;

    ${mediaQueriesBP.maxMD} {
        padding: 32px 0 29px;
    }
`;

const blogRichContentStyles = css`
    order: 3;
    padding-top: 0;

    ${mediaQueriesBP.maxMD} {
        p,
        a,
        span,
        ul,
        ol,
        li,
        strong,
        i {
            font-size: 14px;
        }
    }

    ${StyledClickable} {
        display: initial;
    }

    ${TypographyWrapper} {
        display: initial;
        border-bottom: 0;

        & > ${TypographyStyle} {
            display: initial;
            border-bottom: 0;
            font-size: inherit;
            line-height: inherit;
        }

        &:hover {
            text-decoration-style: double;
        }
    }
`;

const postInfo = css`
    padding-top: 12px;

    ${mediaQueriesBP.maxMD} {
        padding-top: 5px;
    }
`;

const blogAuthorLabel = css`
    font-size: 14px;
    text-transform: none;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.71;
    letter-spacing: normal;
    font-family: ${fontFamilyAlt};

    ${mediaQueriesBP.maxMD} {
        &&& {
            font-size: 12px;
        }
    }
`;

const rightBottomContentStyles = css`
    ${ContentWrapper} {
        p {
            font-size: 21px;
            font-family: ${fontFamily};
        }
    }

    .link-list__widget {
        span {
            font-size: 16px;
            margin-bottom: 5px;
        }
    }

    ${mediaQueriesBP.maxMD} {
        order: 5;
    }
    ${mediaQueriesBP.minMD} {
        order: 4;
        padding-top: 0;
        &&&& {
            margin-top: -4px;
        }
    }
`;

const bottomContentStyles = css`
    ${mediaQueriesBP.maxMD} {
        order: 4;
    }
    ${mediaQueriesBP.minMD} {
        order: 5;
    }
`;

const BlogDetailPage = ({ id, fields: { author, content, publishDate, title } }: OwnProps) => {
    const dateOptions: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
    const dateTimeFormat = new Intl.DateTimeFormat("en-US", dateOptions);
    const { isInShell } = useShellContext();

    // find the first image in the content
    const firstImage = content.match(/<img[^>]+>/);
    const firstImageSrc = firstImage && firstImage[0].match(/src="([^"]+)"/);
    const firstImageSrcValue = firstImageSrc && firstImageSrc[1];

    // set the data for the schema using fields info
    const schemaData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": isInShell ? "" : window.location.href,
        },
        headline: title,
        image: [firstImageSrcValue],
        datePublished: publishDate,
        dateModified: publishDate,
        author: {
            "@type": "Person",
            name: author,
        },
        publisher: {
            "@type": "Organization",
            name: "Waytek",
            logo: {
                "@type": "ImageObject",
                url: "https://info.waytekwire.com/hs-fs/hubfs/Logos/Waytek_CMYK_V_SM_REG.png",
            },
        },
    };

    return (
        <MobiusPage>
            <SchemaForBlogDetail location={["BlogDetailPage"]} id="SchemaBlogDetail" postData={schemaData} />
            <GridContainer gap={40}>
                <GridItem width={[12, 12, 8, 8, 8]} css={blogTitleStyles}>
                    <Typography css={pageTitle} as="h1" variant="h3" data-test-selector="PageTitle">
                        {title}
                    </Typography>
                </GridItem>
                <GridItem
                    width={[0, 0, 4, 4, 4]}
                    css={css`
                        order: 2;
                        padding-top: 0;
                        padding-bottom: 0;
                    `}
                ></GridItem>

                <GridItem width={[12, 12, 8, 8, 8]} css={blogRichContentStyles}>
                    <GridContainer gap={0.000000001}>
                        <GridItem width={12} css={postInfo}>
                            <Typography variant="h5" forwardAs="span" css={blogAuthorLabel}>
                                {publishDate && translate("Posted on")}{" "}
                                {publishDate && dateTimeFormat.format(new Date(publishDate))} {author && `by ${author}`}{" "}
                            </Typography>
                        </GridItem>

                        <GridItem width={12} css={blogRichContentStyles}>
                            <ContentWrapper backgroundColor="transparent" padding={0}>
                                {parse(content, parserOptions)}
                            </ContentWrapper>
                        </GridItem>

                        <GridItem width={12}>
                            <Zone contentId={id} zoneName="ExtraLeftContent"></Zone>
                        </GridItem>
                    </GridContainer>
                </GridItem>

                <GridItem width={[12, 12, 4, 4, 4]} css={rightBottomContentStyles}>
                    <GridContainer gap={0.000000001}>
                        <GridItem width={12}>
                            <Zone contentId={id} zoneName="RightBottomZone"></Zone>
                        </GridItem>
                    </GridContainer>
                </GridItem>

                <GridItem width={12} css={bottomContentStyles}>
                    <GridContainer gap={0.000000001}>
                        <GridItem width={12}>
                            <Zone contentId={id} zoneName="Content"></Zone>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </MobiusPage>
    );
};
const pageModule: PageModule = {
    component: BlogDetailPage,
    definition: {
        displayName: "Blog Detail",
        pageType: "Content",
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        allowedParents: ["BlogCategoryPage"],
        allowedChildren: [],
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            {
                defaultValue: "",
                name: fields.author,
                displayName: "Author",
                fieldType: "General",
                editorTemplate: "SystemListDropDownField",
                systemListName: "Authors",
            },
            {
                name: fields.publishDate,
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.content,
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Contextual",
                isRequired: true,
                collapsedToolbarButtons: {
                    moreText: {
                        buttons: ["paragraphFormat", "bold", "italic", "html"],
                        buttonsVisible: 4,
                    },
                },
            },
            {
                name: fields.summary,
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                isRequired: false,
                extendedConfig: {
                    zIndex: 0,
                },
            },
            {
                name: fields.thumbnail,
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "General",
                isRequired: false,
            },
        ],
    },
};

export default pageModule;
